/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	Control,
	FormProvider,
	SubmitHandler,
	useForm,
	useFormState
} from "react-hook-form";
import { ArrowLeft, Search, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "src/components/ui/button";
import {
	FormField,
	FormItem,
	FormLabel,
	FormControl
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import { Checkbox } from "src/components/ui/checkbox";
import isEmpty from "lodash.isempty";
import { EventFormValues } from "../../adapters/formValuesToEvent";
import { safeImageUrl } from "src/utils/safeImageUrl";
import useAdminHashtags from "src/features/Hashtags/hooks/useAdminHashtags";

export type { EventFormValues };

interface Props {
	defaultValues: EventFormValues;
	onSubmit: (data: EventFormValues, imageRemoved: boolean) => void;
	isSubmitting?: boolean;
	isEdit?: boolean;
	onDelete?: () => void;
	existingImageUrl?: string | null;
}

interface TagCardProps {
	control: Control<EventFormValues>;
}

const TagCard: FC<TagCardProps> = ({ control }) => {
	const { data: hashtags = [] } = useAdminHashtags();
	const [searchTerm, setSearchTerm] = useState("");

	return (
		<FormField
			control={control}
			name="tag"
			render={({ field }) => {
				const selectedHashtag = hashtags.find((h) => h.uuid === field.value);
				const filtered = searchTerm
					? hashtags.filter((h) =>
							h.name.toLowerCase().includes(searchTerm.toLowerCase())
						)
					: [];

				return (
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-1 block">
								Tag
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								Link this event to a social feed hashtag
							</p>

							<div className="max-w-[480px]">
								{selectedHashtag ? (
									<div className="flex items-center gap-2">
										<div className="flex-1 flex items-center bg-white border border-border rounded-lg px-3 py-2 shadow-xs">
											<span className="text-muted-foreground mr-1">#</span>
											<span className="text-sm text-foreground font-medium">
												{selectedHashtag.name}
											</span>
										</div>
										<button
											type="button"
											className="h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-white hover:bg-muted transition-colors shadow-xs"
											aria-label="Remove tag"
											onClick={() => {
												field.onChange(null);
												setSearchTerm("");
											}}
										>
											<X className="h-4 w-4 text-muted-foreground" />
										</button>
									</div>
								) : (
									<div>
										<div className="relative">
											<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
											<input
												type="text"
												value={searchTerm}
												onChange={(e) => setSearchTerm(e.target.value)}
												placeholder="Search hashtags..."
												aria-label="Search hashtags"
												className="w-full bg-white border border-border rounded-lg pl-9 pr-4 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
											/>
										</div>

										{filtered.length > 0 && searchTerm && (
											<ul
												role="listbox"
												aria-label="Hashtag results"
												className="mt-2 bg-white border border-border rounded-lg shadow-md max-h-60 overflow-y-auto"
											>
												{filtered.map((hashtag) => (
													<li
														key={hashtag.uuid}
														role="option"
														aria-selected={false}
														tabIndex={0}
														className="px-3 py-2.5 cursor-pointer hover:bg-primary-lighter text-sm text-foreground transition-colors first:rounded-t-lg last:rounded-b-lg focus:bg-primary-lighter focus:outline-none"
														onClick={() => {
															field.onChange(hashtag.uuid);
															setSearchTerm("");
														}}
														onKeyDown={(e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																field.onChange(hashtag.uuid);
																setSearchTerm("");
															}
														}}
													>
														#{hashtag.name}
													</li>
												))}
											</ul>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				);
			}}
		/>
	);
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const MAX_IMAGE_SIZE = 1_048_576; // 1 MB

const EventForm: FC<Props> = ({
	defaultValues,
	onSubmit,
	isSubmitting = false,
	isEdit = false,
	onDelete,
	existingImageUrl
}) => {
	const methods = useForm<EventFormValues>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues
	});

	const { reset, control, handleSubmit, watch, setValue, trigger } = methods;
	const { isDirty, errors } = useFormState({ control });

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [imageError, setImageError] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const imageFile = watch("image");

	const safeExistingUrl = safeImageUrl(existingImageUrl);
	const displayUrl = imageRemoved ? null : previewUrl || safeExistingUrl;

	const isSubmitBtnDisabled =
		(!isDirty && !imageRemoved) ||
		!isEmpty(errors) ||
		isSubmitting ||
		!!imageError;

	useEffect(() => {
		reset(defaultValues);
		setImageRemoved(false);
		setImageError(null);
	}, [defaultValues]);

	const startDate = watch("startDate");
	const startTime = watch("startTime");
	const endTime = watch("endTime");

	useEffect(() => {
		trigger("endDate");
	}, [startDate, startTime, endTime, trigger]);

	useEffect(() => {
		if (imageFile) {
			const url = URL.createObjectURL(imageFile);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
		}
	}, [imageFile]);

	const validateAndSetFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setImageError("Only JPG and PNG files are allowed.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setImageError("Image must be under 1 MB.");
			if (fileInputRef.current) fileInputRef.current.value = "";
			return;
		}
		setImageError(null);
		setValue("image", file, { shouldDirty: true });
		setImageRemoved(false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) validateAndSetFile(file);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndSetFile(file);
	};

	const handleRemoveImage = () => {
		setValue("image", null, { shouldDirty: true });
		setPreviewUrl(null);
		setImageRemoved(true);
		setImageError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<form
			onSubmit={handleSubmit((data) => onSubmit(data, imageRemoved))}
			onKeyDown={(e) => {
				if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
					e.preventDefault();
				}
			}}
		>
			<FormProvider {...methods}>
				<div className="flex flex-col gap-4">
					{/* Details */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.name ? "border-red-400/60" : "border-border"
						].join(" ")}
					>
						{errors.name && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									Please fill out the required field
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Details
							</label>

							<div className="flex flex-col gap-5">
								<FormField
									control={control}
									name="name"
									rules={{
										required: "Name is required",
										maxLength: {
											value: 100,
											message: "Maximum 100 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Name
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Event name"
													maxLength={100}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 100
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="location"
									rules={{
										maxLength: {
											value: 200,
											message: "Maximum 200 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Location
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Venue name"
													maxLength={200}
													className="w-full bg-white"
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 200
											</p>
										</FormItem>
									)}
								/>

								<FormField
									control={control}
									name="body"
									rules={{
										maxLength: {
											value: 2000,
											message: "Maximum 2000 characters"
										}
									}}
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-sm font-medium text-zinc-600">
												Description
											</FormLabel>
											<FormControl>
												<textarea
													placeholder="Event description"
													maxLength={2000}
													rows={4}
													className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden resize-none"
													onInput={(e) => {
														const target = e.target as HTMLTextAreaElement;
														target.style.height = "auto";
														target.style.height = `${target.scrollHeight}px`;
													}}
													{...field}
												/>
											</FormControl>
											<p className="text-[13px] text-zinc-500 text-right mt-1">
												{field.value?.length || 0} / 2000
											</p>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{/* Schedule */}
					<div
						className={[
							"rounded-xl border bg-subtle shadow-sm transition-colors",
							errors.startDate || errors.endDate
								? "border-red-400/60"
								: "border-border"
						].join(" ")}
					>
						{(errors.startDate || errors.endDate) && (
							<div className="relative">
								<span
									role="alert"
									className="absolute -top-2.5 right-3 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium"
								>
									{errors.endDate?.message ||
										"Please fill out the required fields"}
								</span>
							</div>
						)}
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Schedule
							</label>

							<div className="flex flex-col gap-5">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<FormField
										control={control}
										name="startDate"
										rules={{
											required: "Start date is required"
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Start date
												</FormLabel>
												<FormControl>
													<Input
														type="date"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>

									<FormField
										control={control}
										name="startTime"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													Start time
												</FormLabel>
												<FormControl>
													<Input
														type="time"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={control}
									name="showStartDate"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Show start date to app users
											</FormLabel>
										</FormItem>
									)}
								/>

								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<FormField
										control={control}
										name="endDate"
										rules={{
											required: "End date is required",
											validate: (value: string) => {
												const startDate = watch("startDate");
												const startTime = watch("startTime");
												const endTime = watch("endTime");
												if (startDate && value) {
													const start = new Date(
														`${startDate}T${startTime || "00:00"}`
													);
													const end = new Date(
														`${value}T${endTime || "00:00"}`
													);
													if (end <= start) {
														return "End date must be after start date";
													}
												}
												return true;
											}
										}}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													End date
												</FormLabel>
												<FormControl>
													<Input
														type="date"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>

									<FormField
										control={control}
										name="endTime"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm font-medium text-zinc-600">
													End time
												</FormLabel>
												<FormControl>
													<Input
														type="time"
														className="w-full bg-white"
														{...field}
													/>
												</FormControl>
											</FormItem>
										)}
									/>
								</div>

								<FormField
									control={control}
									name="showEndDate"
									render={({ field }) => (
										<FormItem className="flex items-center gap-2">
											<FormControl>
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
											<FormLabel className="!mt-0 text-sm text-zinc-700 cursor-pointer">
												Show end date to app users
											</FormLabel>
										</FormItem>
									)}
								/>
							</div>
						</div>
					</div>

					{/* Tag */}
					<TagCard control={control} />

					{/* Image */}
					<div className="rounded-xl border border-border bg-subtle shadow-sm">
						<div className="p-6">
							<label className="font-heading text-lg font-medium text-foreground mb-4 block">
								Image (optional)
							</label>
							<p className="text-[13px] text-muted-foreground mb-4">
								JPG or PNG, 1000 x 566px recommended, under 1 MB
							</p>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/jpg,image/png"
								className="hidden"
								onChange={handleFileChange}
							/>
							<div className="max-w-[480px]">
								{displayUrl ? (
									<>
										<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
											<img
												src={displayUrl}
												alt="Uploaded image preview"
												className="object-contain w-full h-auto max-h-[400px]"
											/>
										</div>
										<div className="flex items-center gap-2">
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
												onClick={() => fileInputRef.current?.click()}
											>
												Change image
											</button>
											<button
												type="button"
												className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
												onClick={handleRemoveImage}
											>
												Remove
											</button>
										</div>
									</>
								) : (
									<div
										role="button"
										tabIndex={0}
										onClick={() => fileInputRef.current?.click()}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												fileInputRef.current?.click();
											}
										}}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										className={[
											"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
											isDragging
												? "border-primary bg-primary-lighter"
												: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
										].join(" ")}
									>
										<Upload className="h-6 w-6 text-muted-foreground" />
										<div>
											<p className="text-sm font-medium text-foreground">
												Drag image here or click to browse
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												JPG or PNG, under 1 MB
											</p>
										</div>
									</div>
								)}
							</div>
							{imageError && (
								<p className="text-sm text-red-500 mt-2">{imageError}</p>
							)}
						</div>
					</div>
				</div>

				<div className="flex items-center justify-between pt-8 mt-8 border-t border-border">
					<div className="flex items-center gap-4">
						<Link
							to="/app/events"
							className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to events
						</Link>

						{isEdit && onDelete && (
							<Button type="button" variant="destructive" onClick={onDelete}>
								Delete
							</Button>
						)}
					</div>

					<Button type="submit" disabled={isSubmitBtnDisabled}>
						{isSubmitting ? (
							<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
						) : (
							"Save"
						)}
					</Button>
				</div>
			</FormProvider>
		</form>
	);
};

export default EventForm;
